import { useEffect } from 'react';
import ReactGA from 'react-ga4';

interface AnalyticsConfig {
  measurementId: string;
  debug?: boolean;
}

interface PageViewParams {
  page_title?: string;
  page_location?: string;
  page_path?: string;
}

interface CustomEventParams {
  [key: string]: string | number | boolean;
}

export const useAnalytics = (config: AnalyticsConfig) => {
  useEffect(() => {
    if (config.measurementId) {
      ReactGA.initialize(config.measurementId, {
        testMode: config.debug,
        gtagOptions: {
          debug_mode: config.debug,
        },
      });

      // Send initial page view
      ReactGA.send({ hitType: 'pageview', page: window.location.pathname });
    }
  }, [config.measurementId, config.debug]);

  const trackPageView = (params?: PageViewParams) => {
    ReactGA.send({
      hitType: 'pageview',
      page_title: params?.page_title || document.title,
      page_location: params?.page_location || window.location.href,
      page_path: params?.page_path || window.location.pathname,
    });
  };

  const trackEvent = (eventName: string, parameters?: CustomEventParams) => {
    ReactGA.event(eventName, parameters);
  };

  // E-commerce events
  const trackPurchase = (
    transactionId: string,
    value: number,
    currency: string = 'USD',
    items: any[]
  ) => {
    ReactGA.event('purchase', {
      transaction_id: transactionId,
      value,
      currency,
      items,
    });
  };

  const trackAddToCart = (currency: string, value: number, items: any[]) => {
    ReactGA.event('add_to_cart', {
      currency,
      value,
      items,
    });
  };

  const trackRemoveFromCart = (
    currency: string,
    value: number,
    items: any[]
  ) => {
    ReactGA.event('remove_from_cart', {
      currency,
      value,
      items,
    });
  };

  const trackViewItem = (currency: string, value: number, items: any[]) => {
    ReactGA.event('view_item', {
      currency,
      value,
      items,
    });
  };

  const trackViewItemList = (itemListName: string, items: any[]) => {
    ReactGA.event('view_item_list', {
      item_list_name: itemListName,
      items,
    });
  };

  const trackSelectItem = (itemListName: string, items: any[]) => {
    ReactGA.event('select_item', {
      item_list_name: itemListName,
      items,
    });
  };

  const trackBeginCheckout = (
    currency: string,
    value: number,
    items: any[]
  ) => {
    ReactGA.event('begin_checkout', {
      currency,
      value,
      items,
    });
  };

  const trackSearch = (searchTerm: string) => {
    ReactGA.event('search', {
      search_term: searchTerm,
    });
  };

  // User engagement events
  const trackSignUp = (method: string) => {
    ReactGA.event('sign_up', {
      method,
    });
  };

  const trackLogin = (method: string) => {
    ReactGA.event('login', {
      method,
    });
  };

  // Custom business events
  const trackRecipeView = (
    recipeId: string,
    recipeName: string,
    category: string
  ) => {
    ReactGA.event('view_recipe', {
      recipe_id: recipeId,
      recipe_name: recipeName,
      category,
    });
  };

  const trackMenuPlanGenerate = (preferences: string[]) => {
    ReactGA.event('generate_menu_plan', {
      preferences: preferences.join(','),
    });
  };

  const trackFilterUsage = (filterType: string, filterValue: string) => {
    ReactGA.event('use_filter', {
      filter_type: filterType,
      filter_value: filterValue,
    });
  };

  const trackLanguageChange = (oldLanguage: string, newLanguage: string) => {
    ReactGA.event('change_language', {
      old_language: oldLanguage,
      new_language: newLanguage,
    });
  };

  const trackNewsletterSignup = (location: string) => {
    ReactGA.event('newsletter_signup', {
      location,
    });
  };

  return {
    trackPageView,
    trackEvent,
    trackPurchase,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewItem,
    trackViewItemList,
    trackSelectItem,
    trackBeginCheckout,
    trackSearch,
    trackSignUp,
    trackLogin,
    trackRecipeView,
    trackMenuPlanGenerate,
    trackFilterUsage,
    trackLanguageChange,
    trackNewsletterSignup,
  };
};
